import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { EmbeddedVideoPlayerService } from '../embedded-video-player.service';
import { DEFAULT_MEDIA_FILTER_PRESET_ID, MediaFilterPresetId } from '../media-filter-presets';
import { computeContainRect, hasRenderableMediaFilterEffects } from '../media-filter-renderer';
import { MediaFilterVideoWebglRenderer } from '../media-filter-video-webgl-renderer';

@Component({
  selector: 'app-native-video-preview',
  templateUrl: './native-video-preview.component.html',
  styleUrls: ['./native-video-preview.component.scss'],
})
export class NativeVideoPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() nativeSrc?: string;
  @Input() fallbackFile?: File;
  @Input() fallbackSrc?: string;
  @Input() enableSkeleton: boolean = true;
  @Input() clickToToggle: boolean = false;
  @Input() height?: string;
  @Input() loop: boolean = true;
  @Input() muted: boolean = true;
  @Input() previewPresetId: MediaFilterPresetId = DEFAULT_MEDIA_FILTER_PRESET_ID;
  @Input() forceFallback: boolean = false;
  @Output() loadingChange = new EventEmitter<boolean>();

  public useNativePreview = false;
  public showSkeleton = true;
  public resolvedHeight?: string | null;
  public resolvedFallbackSrc: string | null = null;
  public fallbackFrameStyle: Record<string, string> | null = null;
  public shouldUseFilteredFallbackCanvas = false;
  public readonly playerId = `native-video-player-${uuidv4()}`;
  private initRequested = false;
  private viewReady = false;
  public readonly isNative = this.embeddedVideoPlayer.isNativePlatform();
  private resizeObserver?: ResizeObserver;
  private viewportListenersBound = false;
  private onViewportChangeBound = () => this.onViewportChange();
  private initRetryTimeout?: number;
  private initAttempts = 0;
  private postInitTimeout?: number;
  private fallbackObjectUrl: string | null = null;
  private filteredFallbackRenderer?: MediaFilterVideoWebglRenderer;
  @ViewChild('host') host?: ElementRef<HTMLElement>;
  @ViewChild('fallbackVideo') fallbackVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('fallbackCanvas') fallbackCanvas?: ElementRef<HTMLCanvasElement>;

  constructor(private readonly embeddedVideoPlayer: EmbeddedVideoPlayerService) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.resolveHeight();
    this.tryInitNativePlayer();
    this.setupResizeObserver();
    this.setupViewportListeners();
    this.syncFallbackRenderMode();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nativeSrc'] && this.viewReady && !this.initRequested) {
      this.tryInitNativePlayer();
    }
    if (changes['forceFallback'] && this.viewReady) {
      if (this.forceFallback) {
        this.teardownNativePreview();
        this.useNativePreview = false;
      } else {
        this.tryInitNativePlayer();
      }
    }
    if (changes['height'] && this.viewReady) {
      this.resolveHeight();
    }

    if (changes['fallbackFile'] || changes['fallbackSrc']) {
      this.updateFallbackSource();
    }

    if (changes['muted'] && this.viewReady) {
      this.syncMutedState();
    }

    if (changes['previewPresetId'] && this.filteredFallbackRenderer) {
      this.filteredFallbackRenderer.setPreset(this.previewPresetId);
    }

    if ((changes['previewPresetId'] || changes['forceFallback'] || changes['fallbackFile'] || changes['fallbackSrc']) && this.viewReady) {
      this.syncFallbackRenderMode();
    }
  }

  async tryInitNativePlayer(): Promise<void> {
    if (!this.isNative || this.forceFallback) {
      this.useNativePreview = false;
      return;
    }

    if (!this.viewReady || this.initRequested || !this.nativeSrc) {
      return;
    }

    this.initRequested = true;
    const rect = this.getAdjustedHostRect();
    if (!rect) {
      this.initRequested = false;
      return;
    }
    if (rect.width <= 1 || rect.height <= 1) {
      this.initAttempts += 1;
      this.initRequested = false;
      if (this.initAttempts < 40) {
        this.initRetryTimeout = window.setTimeout(() => this.tryInitNativePlayer(), 50);
      }
      return;
    }

    const initialized = await this.embeddedVideoPlayer.createPlayer({
      playerId: this.playerId,
      url: this.nativeSrc,
      rect,
      autoplay: true,
      loop: this.loop,
      muted: this.muted,
    });
    this.useNativePreview = initialized;
    this.setLoading(!initialized);
    this.initAttempts = 0;

    if (initialized) {
      this.postInitTimeout = window.setTimeout(() => {
        const updatedRect = this.getAdjustedHostRect();
        if (updatedRect) {
          this.embeddedVideoPlayer.updateLayout(this.playerId, updatedRect);
        }
      }, 100);
    }
  }

  onFallbackLoaded(): void {
    this.setLoading(false);
    this.updateFallbackFrameStyle();
    this.syncFallbackRenderMode();
    const video = this.fallbackVideo?.nativeElement;
    if (video) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }

  onFallbackError(): void {
    this.setLoading(false);
  }

  toggleFallback(): void {
    if (!this.clickToToggle) {
      return;
    }

    const video = this.fallbackVideo?.nativeElement;
    if (!video) {
      return;
    }

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.viewportListenersBound && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewportChangeBound);
      window.visualViewport.removeEventListener('scroll', this.onViewportChangeBound);
    }
    if (this.initRetryTimeout) {
      window.clearTimeout(this.initRetryTimeout);
    }
    if (this.postInitTimeout) {
      window.clearTimeout(this.postInitTimeout);
    }
    this.destroyFilteredFallbackRenderer();
    this.releaseFallbackSource();
    this.teardownNativePreview();
  }

  private syncMutedState(): void {
    const fallbackVideo = this.fallbackVideo?.nativeElement;
    if (fallbackVideo) {
      fallbackVideo.muted = this.muted;
    }

    if (!this.useNativePreview) {
      return;
    }

    void this.embeddedVideoPlayer.setMuted(this.playerId, this.muted);
  }

  private updateFallbackSource(): void {
    this.destroyFilteredFallbackRenderer();
    this.releaseFallbackSource();
    this.fallbackFrameStyle = null;
    if (this.fallbackSrc) {
      this.resolvedFallbackSrc = this.fallbackSrc;
      this.setLoading(true);
      return;
    }

    if (!this.fallbackFile) {
      this.resolvedFallbackSrc = null;
      this.setLoading(false);
      return;
    }

    this.fallbackObjectUrl = URL.createObjectURL(this.fallbackFile);
    this.resolvedFallbackSrc = this.fallbackObjectUrl;
    this.setLoading(true);
  }

  private releaseFallbackSource(): void {
    if (!this.fallbackObjectUrl) {
      return;
    }
    URL.revokeObjectURL(this.fallbackObjectUrl);
    this.fallbackObjectUrl = null;
  }

  private setLoading(isLoading: boolean): void {
    this.showSkeleton = isLoading;
    Promise.resolve().then(() => this.loadingChange.emit(isLoading));
  }

  private teardownNativePreview(): void {
    this.initRequested = false;
    this.embeddedVideoPlayer.destroy(this.playerId);
  }

  private setupResizeObserver(): void {
    if (!this.host?.nativeElement) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateFallbackFrameStyle();
      this.refreshFilteredFallbackLayout();
      const rect = this.getAdjustedHostRect();
      if (!rect) {
        return;
      }

      if (this.isNative && !this.useNativePreview && !this.initRequested && rect.width > 1 && rect.height > 1) {
        this.tryInitNativePlayer();
        return;
      }

      if (this.useNativePreview) {
        this.embeddedVideoPlayer.updateLayout(this.playerId, rect);
      }
    });
    this.resizeObserver.observe(this.host.nativeElement);
  }

  private setupViewportListeners(): void {
    if (!this.isNative || this.viewportListenersBound) {
      return;
    }
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    vv.addEventListener('resize', this.onViewportChangeBound, { passive: true });
    vv.addEventListener('scroll', this.onViewportChangeBound, { passive: true });
    this.viewportListenersBound = true;
  }

  private onViewportChange(): void {
    this.updateFallbackFrameStyle();
    this.refreshFilteredFallbackLayout();
    if (!this.useNativePreview) {
      return;
    }
    this.resolveHeight();
    const rect = this.getAdjustedHostRect();
    if (!rect) {
      return;
    }
    this.embeddedVideoPlayer.updateLayout(this.playerId, rect);
  }

  private resolveHeight(): void {
    if (!this.height) {
      this.resolvedHeight = null;
      return;
    }
    if (!this.isNative) {
      this.resolvedHeight = null;
      return;
    }
    const normalized = this.height.trim();
    if (normalized.endsWith('vh')) {
      const factor = Number.parseFloat(normalized.replace('vh', '').trim());
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      if (Number.isFinite(factor) && viewportHeight) {
        const px = Math.max(0, Math.round((factor / 100) * viewportHeight));
        this.resolvedHeight = `${px}px`;
        return;
      }
    }
    this.resolvedHeight = null;
  }

  private getAdjustedHostRect(): DOMRect | null {
    if (!this.host?.nativeElement) {
      return null;
    }
    const rect = this.host.nativeElement.getBoundingClientRect();
    const vv = window.visualViewport;
    if (!vv) {
      return rect;
    }
    const adjusted = {
      x: rect.x + vv.offsetLeft,
      y: rect.y + vv.offsetTop,
      width: rect.width,
      height: rect.height,
      top: rect.top + vv.offsetTop,
      right: rect.right + vv.offsetLeft,
      bottom: rect.bottom + vv.offsetTop,
      left: rect.left + vv.offsetLeft,
    };
    return adjusted as DOMRect;
  }
  private updateFallbackFrameStyle(): void {
    const video = this.fallbackVideo?.nativeElement;
    const root = this.host?.nativeElement?.parentElement;
    if (!video || !root) {
      this.fallbackFrameStyle = null;
      return;
    }

    const sourceWidth = video.videoWidth || 0;
    const sourceHeight = video.videoHeight || 0;
    const containerWidth = root.clientWidth || 0;
    const containerHeight = root.clientHeight || 0;

    if (!sourceWidth || !sourceHeight || !containerWidth || !containerHeight) {
      this.fallbackFrameStyle = null;
      return;
    }

    const rect = computeContainRect(sourceWidth, sourceHeight, containerWidth, containerHeight);
    if (!rect) {
      this.fallbackFrameStyle = null;
      return;
    }

    this.fallbackFrameStyle = {
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      left: `${rect.left}px`,
      top: `${rect.top}px`,
    };
  }

  private syncFallbackRenderMode(): void {
    this.shouldUseFilteredFallbackCanvas = this.forceFallback && hasRenderableMediaFilterEffects(this.previewPresetId);

    if (this.shouldUseFilteredFallbackCanvas) {
      this.queueFilteredFallbackRendererStart();
      return;
    }

    this.destroyFilteredFallbackRenderer();
  }

  private queueFilteredFallbackRendererStart(): void {
    window.requestAnimationFrame(() => {
      this.startFilteredFallbackRenderer();
    });
  }

  private startFilteredFallbackRenderer(): void {
    if (!this.shouldUseFilteredFallbackCanvas) {
      return;
    }

    const video = this.fallbackVideo?.nativeElement;
    const canvas = this.fallbackCanvas?.nativeElement;
    if (!video || !canvas || video.readyState < 2) {
      return;
    }

    if (!this.filteredFallbackRenderer) {
      this.filteredFallbackRenderer = new MediaFilterVideoWebglRenderer(canvas, video);
    }

    this.filteredFallbackRenderer.setPreset(this.previewPresetId);
    const started = this.filteredFallbackRenderer.start();
    if (!started) {
      this.destroyFilteredFallbackRenderer();
      this.shouldUseFilteredFallbackCanvas = false;
      return;
    }

    this.refreshFilteredFallbackLayout();
  }

  private refreshFilteredFallbackLayout(): void {
    if (!this.filteredFallbackRenderer) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.filteredFallbackRenderer?.resize();
      this.filteredFallbackRenderer?.setPreset(this.previewPresetId);
      this.filteredFallbackRenderer?.drawFrame();
    });
  }

  private destroyFilteredFallbackRenderer(): void {
    this.filteredFallbackRenderer?.destroy();
    this.filteredFallbackRenderer = undefined;
  }
}
